import { Injectable } from '@angular/core';
import { BrowserInfo, BrowserSupportResult } from '../models/browser.model';

@Injectable({
  providedIn: 'root'
})
export class BrowserCompatibilityService {

  private readonly MINIMUM_VERSIONS = {
    chrome: 88,
    firefox: 78,
    safari: 14,
    edge: 88,
    opera: 74,
    chrome_ios: 111,
    firefox_ios: 102,
    safari_ios: 16,
    edge_ios: 111,
  };

  checkBrowserSupport(): BrowserSupportResult {
    const browserInfo = this.getBrowserInfo();
    const isSupported = this.isVersionSupported(browserInfo);
    const hasRequiredFeatures = this.checkRequiredFeatures();

    return {
      isSupported: isSupported && hasRequiredFeatures,
      browserName: browserInfo.name,
      browserVersion: browserInfo.version,
      missingFeatures: hasRequiredFeatures ? [] : this.getMissingFeatures(),
      recommendation: this.getRecommendation(browserInfo, isSupported, hasRequiredFeatures)
    };
  }

  private getBrowserInfo(): BrowserInfo {
    const userAgent = navigator.userAgent;

    // iOS detection
    const isIOS = /iPhone|iPad|iPod/.test(userAgent);

    // Chrome on iOS (CriOS)
    if (isIOS && /CriOS\/(\d+)/.test(userAgent)) {
      const version = parseInt(userAgent.match(/CriOS\/(\d+)/)![1]);
      return { name: 'chrome_ios', version };
    }

    // Edge on iOS (EdgiOS)
    if (isIOS && /EdgiOS\/(\d+)/.test(userAgent)) {
      const version = parseInt(userAgent.match(/EdgiOS\/(\d+)/)![1]);
      return { name: 'edge_ios', version };
    }

    // Firefox on iOS (FxiOS)
    if (isIOS && /FxiOS\/(\d+)/.test(userAgent)) {
      const version = parseInt(userAgent.match(/FxiOS\/(\d+)/)![1]);
      return { name: 'firefox_ios', version };
    }

    // Safari (default browser on iOS)
    if (
      isIOS &&
      /Version\/(\d+)/.test(userAgent) &&
      /Safari/.test(userAgent)
    ) {
      const version = parseInt(userAgent.match(/Version\/(\d+)/)![1]);
      return { name: 'safari_ios', version };
    }
    // Chrome
    if (/Chrome\/(\d+)/.test(userAgent) && !/Edg\//.test(userAgent)) {
      const version = parseInt(userAgent.match(/Chrome\/(\d+)/)![1]);
      return { name: 'chrome', version };
    }

    // Edge (Chromium-based)
    if (/Edg\/(\d+)/.test(userAgent)) {
      const version = parseInt(userAgent.match(/Edg\/(\d+)/)![1]);
      return { name: 'edge', version };
    }

    // Firefox
    if (/Firefox\/(\d+)/.test(userAgent)) {
      const version = parseInt(userAgent.match(/Firefox\/(\d+)/)![1]);
      return { name: 'firefox', version };
    }

    // Safari
    if (/Safari\//.test(userAgent) && !/Chrome\//.test(userAgent)) {
      const versionMatch = userAgent.match(/Version\/(\d+)/);
      const version = versionMatch ? parseInt(versionMatch[1]) : 0;
      return { name: 'safari', version };
    }

    // Opera
    if (/OPR\/(\d+)/.test(userAgent)) {
      const version = parseInt(userAgent.match(/OPR\/(\d+)/)![1]);
      return { name: 'opera', version };
    }

    // Internet Explorer (not supported)
    if (/MSIE|Trident/.test(userAgent)) {
      return { name: 'ie', version: 0 };
    }

    return { name: 'unknown', version: 0 };
  }

  private isVersionSupported(browserInfo: BrowserInfo): boolean {
    if (browserInfo.name === 'ie' || browserInfo.name === 'unknown') {
      return false;
    }

    const minimumVersion = this.MINIMUM_VERSIONS[browserInfo.name as keyof typeof this.MINIMUM_VERSIONS];
    return browserInfo.version >= minimumVersion;
  }

  private checkRequiredFeatures(): boolean {
    const requiredFeatures = [
      'Promise',
      'fetch',
      'Map',
      'Set',
      'WeakMap',
      'Symbol',
      'Proxy',
      'CustomEvent',
      'addEventListener'
    ];

    return requiredFeatures.every(feature => {
      try {
        return this.featureExists(feature);
      } catch {
        return false;
      }
    });
  }

  private featureExists(feature: string): boolean {
    switch (feature) {
      case 'Promise':
        return typeof Promise !== 'undefined';
      case 'fetch':
        return typeof fetch !== 'undefined';
      case 'Map':
        return typeof Map !== 'undefined';
      case 'Set':
        return typeof Set !== 'undefined';
      case 'WeakMap':
        return typeof WeakMap !== 'undefined';
      case 'Symbol':
        return typeof Symbol !== 'undefined';
      case 'Proxy':
        return typeof Proxy !== 'undefined';
      case 'CustomEvent':
        return typeof CustomEvent !== 'undefined';
      case 'addEventListener':
        return typeof document !== 'undefined' &&
          typeof document.addEventListener === 'function';
      default:
        return false;
    }
  }

  private getMissingFeatures(): string[] {
    const features = ['Promise', 'fetch', 'Map', 'Set', 'WeakMap', 'Symbol', 'Proxy', 'CustomEvent'];
    return features.filter(feature => !this.featureExists(feature));
  }

  private getRecommendation(browserInfo: BrowserInfo, isVersionSupported: boolean, hasRequiredFeatures: boolean): string {
    if (browserInfo.name === 'ie') {
      return 'Internet Explorer is not supported. Please upgrade to a modern browser like Chrome, Firefox, Safari, or Edge.';
    }

    if (browserInfo.name === 'unknown') {
      return 'Your browser could not be identified. Please ensure you are using a modern, up-to-date browser.';
    }

    if (!isVersionSupported) {
      const minVersion = this.MINIMUM_VERSIONS[browserInfo.name as keyof typeof this.MINIMUM_VERSIONS];
      return `Your ${browserInfo.name} version (${browserInfo.version}) is outdated. Please upgrade to version ${minVersion} or later.`;
    }

    if (!hasRequiredFeatures) {
      return 'Your browser is missing required features for this application. Please update to the latest version.';
    }

    return 'Your browser is fully supported!';
  }
}
